import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import type {
  ResearchSettings,
  ResearchSettingsField,
  ResearchSettingsFieldGroup,
  ResearchSettingsOverridePayload,
  ResearchSettingsResponse,
} from '../types';

// Build the editor state from a server response. Override values stay null
// so the input renders the built-in default as a placeholder.
function buildDraft(
  override: ResearchSettingsResponse['override'],
): ResearchSettingsOverridePayload {
  const out: ResearchSettingsOverridePayload = {};
  for (const k of Object.keys(override) as ResearchSettingsField[]) {
    out[k] = typeof override[k] === 'number' ? override[k] : null;
  }
  return out;
}

function isCustomized(
  override: ResearchSettingsResponse['override'],
): boolean {
  return Object.values(override).some((v) => v !== null && v !== undefined);
}

export default function PageBuilderResearchSettings() {
  const [data, setData] = useState<ResearchSettingsResponse | null>(null);
  const [draft, setDraft] = useState<ResearchSettingsOverridePayload>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getResearchSettings()
      .then((res) => {
        if (!active) return;
        setData(res);
        setDraft(buildDraft(res.override));
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const customized = useMemo(
    () => (data ? isCustomized(data.override) : false),
    [data],
  );

  const handleField = (key: ResearchSettingsField, raw: string) => {
    setDraft((d) => ({
      ...d,
      [key]: raw === '' ? null : Number(raw),
    }));
    setFlash(null);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setFlash(null);
    try {
      const next = await api.updateResearchSettings(draft);
      setData(next);
      setDraft(buildDraft(next.override));
      setFlash('Saved');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Revert all knobs to built-in defaults?')) return;
    setSaving(true);
    setError(null);
    setFlash(null);
    try {
      const next = await api.resetResearchSettings();
      setData(next);
      setDraft(buildDraft(next.override));
      setFlash('Reverted to defaults');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 py-6">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Page builder research settings
          </h2>
          {customized && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
              Customized
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Per-user knobs for the page-builder agent. Leave any field blank to
          use the built-in default (shown as a placeholder). The agent reads
          these once at the start of every build, so changes apply to the
          next page you build or outline you generate.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}
      {flash && (
        <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded px-3 py-2">
          {flash}
        </div>
      )}

      <div className="space-y-4">
        {data.field_groups.map((group: ResearchSettingsFieldGroup) => (
          <section
            key={group.key}
            className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"
          >
            <header className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                {group.label}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{group.description}</p>
            </header>
            <div className="px-3 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {group.fields.map((f) => {
                const value = draft[f.key];
                const builtin = data.builtin[f.key as keyof ResearchSettings];
                const effective = data.effective[
                  f.key as keyof ResearchSettings
                ];
                return (
                  <label key={f.key} className="block">
                    <span className="block text-xs text-gray-700 dark:text-gray-300 mb-0.5">
                      {f.label}
                    </span>
                    <input
                      type="number"
                      min={f.min}
                      max={f.max}
                      value={
                        value === null || value === undefined
                          ? ''
                          : String(value)
                      }
                      placeholder={String(builtin)}
                      onChange={(e) => handleField(f.key, e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="block text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                      default {builtin}
                      {effective !== builtin && value !== null && (
                        <> · effective {effective}</>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {customized && (
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
          >
            Reset all to defaults
          </button>
        )}
      </div>
    </div>
  );
}
